// RE₿EL by Eddy — landing page with 3 sections (2026-02).
//   1. Biografía — bio + photo (editable inline by eddy_admin only)
//   2. Publicaciones — isolated feed on channel="eddy"
//   3. Cocina en Vivo — YouTube embed (URL editable by eddy_admin)
//
// Access is public for viewing. Editing controls only render when
// the current user is is_eddy_admin (backend authoritative).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Pencil, Save, X, ImagePlus, Trash2, Loader2, Play, MessageCircle, Send, ChevronDown } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";
import { AnalysisSocialBar } from "../components/shadow/AnalysisSocialBar";
import { SharePost } from "../components/SharePost";
import { useLanguage } from "../i18n/LanguageContext";
import { EDDY_I18N } from "../i18n/eddy";
import API from "../lib/api";

// Resolve a locale-aware editable string with a 3-tier fallback:
//   1) profile.<field>_i18n[lang]  — Eddy edited that language explicitly
//   2) profile.<field>              — legacy monolingual (Eddy edited pre-i18n)
//   3) canonical default from EDDY_I18N
// Same helper handles arrays (services). Never throws.
const resolveI18n = (profile, i18nKey, legacyKey, lang, fallback) => {
  const bucket = profile?.[i18nKey] || {};
  const perLocale = bucket[lang];
  if (perLocale !== undefined && perLocale !== null && (
      (typeof perLocale === "string" && perLocale.trim() !== "") ||
      (Array.isArray(perLocale) && perLocale.length > 0)
    )) {
    return perLocale;
  }
  const legacy = profile?.[legacyKey];
  if (legacy !== undefined && legacy !== null && (
      (typeof legacy === "string" && legacy.trim() !== "") ||
      (Array.isArray(legacy) && legacy.length > 0)
    )) {
    return legacy;
  }
  return fallback;
};

const bioPlaceholderFallback =
  "Aquí Eddy contará su historia: cómo empezó, qué le llevó al mundo cripto, su trayectoria (La Comisaría Valencia, Sandals Resorts International, etc.), sus proyectos y su filosofía RE₿EL. (El admin de la sección puede editar este texto).";

const RebelByEddyPage = () => {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const t = EDDY_I18N[lang] || EDDY_I18N.en || EDDY_I18N.es;
  const navigate = useNavigate();  // eslint-disable-line no-unused-vars

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [postsPage, setPostsPage] = useState(1);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsHasMore, setPostsHasMore] = useState(false);

  const isEddyAdmin = !!(profile?.viewer_is_eddy_admin || user?.is_eddy_admin);

  // ── initial load ────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/eddy/profile`, { withCredentials: true })
      .then(({ data }) => {
        if (alive) setProfile(data);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [user?.id]);

  const loadPosts = useCallback(async (page) => {
    setPostsLoading(true);
    try {
      const { data } = await axios.get(`${API}/eddy/feed?page=${page}&limit=12`);
      setPosts((prev) => (page === 1 ? data.posts : [...prev, ...data.posts]));
      setPostsPage(page);
      setPostsHasMore(!!data.has_more);
    } catch (e) {
      // silent
    } finally {
      setPostsLoading(false);
    }
  }, []);

  useEffect(() => { loadPosts(1); }, [loadPosts]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="rebel-by-eddy-page">
      <Navbar />

      {/* ── HERO / breadcrumbs ─────────────────────────────────── */}
      <header className="relative border-b border-white/5 bg-gradient-to-b from-amber-950/20 via-black to-black">
        {/* Banner (foto de portada) — clickable-upload for eddy_admin */}
        <EddyBanner
          profile={profile}
          isEddyAdmin={isEddyAdmin}
          t={t}
          onUpdate={(newProfile) => setProfile((p) => ({ ...(p || {}), ...newProfile }))}
        />
        <div className="relative mx-auto max-w-5xl px-4 pb-10 pt-10 md:px-8">
          <div className="flex items-center justify-between gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/45 transition-colors hover:text-white"
              data-testid="eddy-back-home"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t.hero_back}
            </Link>
            {isEddyAdmin && (
              <Link
                to="/rebel-by-eddy/dashboard"
                data-testid="eddy-public-dashboard-cta"
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/[0.08] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-amber-200 backdrop-blur-md transition hover:bg-amber-500/[0.16]"
              >
                {t.hero_dashboard_cta}
              </Link>
            )}
          </div>
          <h1 className="mt-4 font-mono text-4xl font-black uppercase tracking-tight text-white sm:text-5xl lg:text-6xl">
            {t.hero_title_prefix} <span className="text-amber-400">{t.hero_title_suffix}</span>
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-white/55">
            {t.hero_sub}
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 md:px-8 space-y-14">
        {/* ═══════════════ 1. BIOGRAFÍA ═══════════════ */}
        <BiografiaSection
          profile={profile}
          loading={loading}
          isEddyAdmin={isEddyAdmin}
          t={t}
          lang={lang}
          onUpdate={(newProfile) => setProfile((p) => ({ ...(p || {}), ...newProfile }))}
        />

        {/* ═══════════════ 2. EPB GLOBAL F&B (consultoría) ═══════════════ */}
        <EpbGlobalSection profile={profile} t={t} lang={lang} />

        {/* ═══════════════ 3. PUBLICACIONES ═══════════════ */}
        <PublicacionesSection
          posts={posts}
          hasMore={postsHasMore}
          loading={postsLoading}
          isEddyAdmin={isEddyAdmin}
          t={t}
          lang={lang}
          onCreate={() => loadPosts(1)}
          onDelete={(id) => setPosts((prev) => prev.filter((p) => p.id !== id))}
          onLoadMore={() => loadPosts(postsPage + 1)}
        />

        {/* ═══════════════ 3. COCINA EN VIVO ═══════════════ */}
        <CocinaEnVivoSection
          profile={profile}
          isEddyAdmin={isEddyAdmin}
          t={t}
          onUpdate={(newProfile) => setProfile((p) => ({ ...(p || {}), ...newProfile }))}
        />
      </main>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// BANNER (foto de portada)
// ══════════════════════════════════════════════════════════════════════
const EddyBanner = ({ profile, isEddyAdmin, t, onUpdate }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/eddy/profile/banner`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdate(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || t.banner_error_default);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div
      data-testid="eddy-banner"
      className="relative h-40 w-full overflow-hidden bg-gradient-to-br from-amber-950/30 via-black to-black sm:h-56 md:h-64"
    >
      {profile?.banner_url ? (
        <img
          src={profile.banner_url}
          alt=""
          className="h-full w-full object-cover opacity-80"
          loading="lazy"
        />
      ) : (
        <div className="pointer-events-none absolute inset-0 opacity-25" style={{
          backgroundImage: "radial-gradient(60% 60% at 30% 40%, rgba(251,191,36,0.15), transparent 70%), radial-gradient(50% 50% at 80% 20%, rgba(217,70,239,0.08), transparent 70%)",
        }} />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black" />
      {isEddyAdmin && (
        <div className="absolute right-4 top-4 z-10">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="eddy-banner-upload-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/50 bg-black/70 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-200 backdrop-blur-md transition hover:bg-amber-500/[0.15] disabled:opacity-60"
          >
            {uploading ? (
              <><Loader2 className="h-3 w-3 animate-spin" /> {t.banner_uploading}</>
            ) : (
              <><ImagePlus className="h-3 w-3" /> {profile?.banner_url ? t.banner_upload_change : t.banner_upload_new}</>
            )}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="eddy-banner-input"
          />
          {err && (
            <p className="mt-1 text-right text-[10px] text-red-400" data-testid="eddy-banner-error">{err}</p>
          )}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// BIOGRAFÍA
// ══════════════════════════════════════════════════════════════════════
const BiografiaSection = ({ profile, loading, isEddyAdmin, t, lang, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  // Locale-aware read: bio_i18n[lang] → legacy bio → i18n default.
  // Nunca renderiza contenido en un idioma que no coincida con `lang`.
  const bio = resolveI18n(profile, "bio_i18n", "bio", lang, "");

  const openEdit = () => { setDraft(bio); setErr(""); setEditing(true); };
  const cancel = () => { setEditing(false); setErr(""); };

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      // Write to bio_i18n[lang] so each locale has its own version. We also
      // clear the legacy `bio` field so it doesn't shadow the new i18n one.
      const nextI18n = { ...(profile?.bio_i18n || {}), [lang]: draft };
      const { data } = await axios.put(
        `${API}/eddy/profile`,
        { bio_i18n: nextI18n, bio: "" },
        { withCredentials: true }
      );
      onUpdate(data);
      setEditing(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || t.bio_error_save);
    } finally {
      setSaving(false);
    }
  };

  const pickPhoto = () => fileRef.current?.click();

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/eddy/profile/photo`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdate(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || t.bio_error_photo);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <section data-testid="eddy-bio-section" className="scroll-mt-24">
      <SectionTitle title={t.bio_section_title} hint={t.bio_section_hint} />
      <div className="mt-6 grid gap-6 rounded-3xl border border-white/8 bg-white/[0.02] p-6 md:grid-cols-[240px_1fr] md:p-8">
        <div className="mx-auto flex w-full max-w-[240px] flex-col items-center gap-3 md:mx-0">
          <div
            className="relative aspect-square w-full overflow-hidden rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-950/40 to-black shadow-[0_0_45px_rgba(251,191,36,0.08)]"
            data-testid="eddy-photo-frame"
          >
            {profile?.photo_url ? (
              <img
                src={profile.photo_url}
                alt="Eddy"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center font-mono text-6xl font-black text-amber-500/40">
                E
              </div>
            )}
            {isEddyAdmin && (
              <button
                type="button"
                onClick={pickPhoto}
                disabled={uploading}
                data-testid="eddy-photo-upload-btn"
                className="absolute inset-x-2 bottom-2 inline-flex items-center justify-center gap-1.5 rounded-lg border border-amber-400/50 bg-black/70 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-200 backdrop-blur-md transition hover:bg-amber-500/[0.15] disabled:opacity-60"
              >
                {uploading ? (
                  <><Loader2 className="h-3 w-3 animate-spin" /> {t.bio_photo_uploading}</>
                ) : (
                  <><ImagePlus className="h-3 w-3" /> {profile?.photo_url ? t.bio_photo_upload_change : t.bio_photo_upload_new}</>
                )}
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => uploadPhoto(e.target.files?.[0])}
            data-testid="eddy-photo-input"
          />
        </div>

        <div className="flex flex-col">
          <div className="mb-2 flex items-center justify-between gap-3">
            <h3 className="font-mono text-[11px] font-black uppercase tracking-[0.22em] text-amber-400">
              {t.bio_header}
            </h3>
            {isEddyAdmin && !editing && (
              <button
                type="button"
                onClick={openEdit}
                data-testid="eddy-bio-edit-btn"
                className="inline-flex items-center gap-1.5 rounded-md border border-white/12 bg-white/[0.04] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/60 transition hover:border-amber-400/40 hover:text-amber-200"
              >
                <Pencil className="h-3 w-3" /> {t.bio_edit_btn}
              </button>
            )}
          </div>

          {loading ? (
            <p className="text-sm text-white/40">{t.bio_loading}</p>
          ) : editing ? (
            <div>
              <textarea
                data-testid="eddy-bio-textarea"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={10}
                maxLength={4000}
                placeholder={t.bio_placeholder || bioPlaceholderFallback}
                className="w-full resize-y rounded-xl border border-amber-400/25 bg-black/40 p-3 text-sm leading-relaxed text-white/85 outline-none focus:border-amber-400/60"
              />
              <div className="mt-1 text-right font-mono text-[10px] text-white/35">
                {draft.length} / 4000
              </div>
              {err && (
                <p className="mt-2 text-xs text-red-400" data-testid="eddy-bio-error">{err}</p>
              )}
              <div className="mt-3 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={cancel}
                  disabled={saving}
                  data-testid="eddy-bio-cancel-btn"
                  className="inline-flex items-center gap-1.5 rounded-md border border-white/12 bg-white/[0.04] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white/60 transition hover:text-white"
                >
                  <X className="h-3 w-3" /> {t.bio_cancel_btn}
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  data-testid="eddy-bio-save-btn"
                  className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/50 bg-amber-500/[0.14] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-200 transition hover:bg-amber-500/[0.22] disabled:opacity-60"
                >
                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                  {t.bio_save_btn}
                </button>
              </div>
            </div>
          ) : (
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-white/75" data-testid="eddy-bio-text">
              {bio || t.bio_default_text || t.bio_placeholder || bioPlaceholderFallback}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════════
// PUBLICACIONES
// ══════════════════════════════════════════════════════════════════════
const PublicacionesSection = ({ posts, hasMore, loading, isEddyAdmin, t, lang, onCreate, onDelete, onLoadMore }) => {
  const [showComposer, setShowComposer] = useState(false);

  return (
    <section data-testid="eddy-posts-section" className="scroll-mt-24">
      <div className="flex items-center justify-between gap-3">
        <SectionTitle title={t.posts_section_title} hint={t.posts_section_hint} />
        {isEddyAdmin && (
          <button
            type="button"
            onClick={() => setShowComposer((v) => !v)}
            data-testid="eddy-composer-toggle"
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/45 bg-amber-500/[0.12] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-200 transition hover:bg-amber-500/[0.2]"
          >
            {showComposer ? t.posts_close_btn : t.posts_new_btn}
          </button>
        )}
      </div>

      {isEddyAdmin && showComposer && (
        <EddyComposer
          t={t}
          onCreated={() => { setShowComposer(false); onCreate(); }}
        />
      )}

      <ul className="mt-6 grid gap-5 md:grid-cols-2">
        {posts.map((p) => (
          <EddyPostCard
            key={p.id}
            post={p}
            isEddyAdmin={isEddyAdmin}
            t={t}
            lang={lang}
            onDelete={onDelete}
          />
        ))}
      </ul>

      {posts.length === 0 && !loading && (
        <div
          data-testid="eddy-posts-empty"
          className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-sm text-white/50"
        >
          {t.posts_empty}
        </div>
      )}

      {hasMore && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loading}
            data-testid="eddy-posts-load-more"
            className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-5 py-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/70 transition hover:border-amber-400/40 hover:text-amber-200"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {t.posts_load_more}
          </button>
        </div>
      )}
    </section>
  );
};

const EddyComposer = ({ t, onCreated }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [ticker, setTicker] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const onFile = (f) => {
    setFile(f || null);
    if (f) {
      const url = URL.createObjectURL(f);
      setPreview(url);
    } else {
      setPreview(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file || title.trim().length < 3 || description.trim().length < 10) {
      setErr(t.composer_err_validation);
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      if (ticker.trim()) fd.append("ticker", ticker.trim().toUpperCase());
      await axios.post(`${API}/eddy/posts`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTitle(""); setDescription(""); setTicker(""); setFile(null); setPreview(null);
      onCreated?.();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || t.composer_err_default);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="eddy-composer-form"
      className="mt-4 space-y-3 rounded-2xl border border-amber-400/25 bg-amber-500/[0.03] p-5"
    >
      <div className="grid gap-3 md:grid-cols-2">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t.composer_title_ph}
          maxLength={120}
          data-testid="eddy-composer-title"
          className="rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
        />
        <input
          type="text"
          value={ticker}
          onChange={(e) => setTicker(e.target.value)}
          placeholder={t.composer_ticker_ph}
          maxLength={12}
          data-testid="eddy-composer-ticker"
          className="rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
        />
      </div>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={t.composer_desc_ph}
        rows={4}
        maxLength={5000}
        data-testid="eddy-composer-description"
        className="w-full resize-y rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
      />
      <div className="flex flex-wrap items-center gap-3">
        <label
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-white/12 bg-white/[0.04] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/70 transition hover:border-amber-400/40 hover:text-amber-200"
        >
          <ImagePlus className="h-3 w-3" />
          {file ? t.composer_change_media : t.composer_add_media}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
            onChange={(e) => onFile(e.target.files?.[0])}
            className="hidden"
            data-testid="eddy-composer-file"
          />
        </label>
        {preview && (
          <img src={preview} alt="preview" className="h-16 w-16 rounded-md border border-white/10 object-cover" />
        )}
      </div>
      {err && <p className="text-xs text-red-400" data-testid="eddy-composer-error">{err}</p>}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={submitting}
          data-testid="eddy-composer-submit"
          className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/55 bg-amber-500/[0.16] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.22em] text-amber-200 transition hover:bg-amber-500/[0.24] disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          {t.composer_publish_btn}
        </button>
      </div>
    </form>
  );
};

const EddyPostCard = ({ post, isEddyAdmin, t, lang, onDelete }) => {
  const { user, openLogin } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(post.comment_count || 0);
  // Locale-aware title/description with 2-tier fallback:
  //   1) post.<field>_i18n[lang] (auto-translated at publish/edit)
  //   2) post.<field> (legacy monolingual)
  const displayTitle = resolveI18n(post, "title_i18n", "title", lang, "");
  const displayDescription = resolveI18n(post, "description_i18n", "description", lang, "");

  const del = async () => {
    if (!confirm(t.posts_delete_confirm)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/eddy/posts/${post.id}`, { withCredentials: true });
      onDelete?.(post.id);
    } catch {
      setDeleting(false);
    }
  };

  return (
    <li
      data-testid={`eddy-post-${post.id}`}
      className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition-colors hover:border-amber-400/25"
    >
      {(post.image_url || post.video_url) && (
        <div className="relative aspect-video w-full overflow-hidden bg-black">
          {post.media_type === "video" && post.video_url ? (
            <video
              src={post.video_url}
              poster={post.image_url}
              controls
              className="h-full w-full object-cover"
            />
          ) : (
            <img
              src={post.image_url}
              alt={displayTitle || ""}
              className="h-full w-full object-cover transition-transform group-hover:scale-105"
              loading="lazy"
            />
          )}
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-base font-bold text-white leading-tight">{displayTitle}</h3>
          {isEddyAdmin && (
            <button
              type="button"
              onClick={del}
              disabled={deleting}
              data-testid={`eddy-post-delete-${post.id}`}
              title={t.delete_tooltip}
              className="shrink-0 rounded-md border border-white/10 p-1 text-white/40 transition hover:border-red-400/40 hover:text-red-300"
            >
              {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
        {post.ticker && (
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400/80">
            {post.ticker}
          </p>
        )}
        {displayDescription && (
          <p className="mt-2 whitespace-pre-wrap text-sm text-white/65">
            {displayDescription}
          </p>
        )}

        {/* Social bar — REUTILIZADO tal cual del Shadow Network:
            like → POST /shadow/social/like
            bookmark → POST /shadow/social/bookmark
            share tracking → POST /shadow/social/share
            Los posts de Eddy viven en la misma colección `analyses` así que
            los endpoints funcionan sin cambios (channel-agnostic). */}
        <div className="mt-3 border-t border-white/5 pt-2">
          <AnalysisSocialBar
            analysisId={post.id}
            initialLiked={!!post.viewer_liked}
            initialBookmarked={!!post.viewer_bookmarked}
            initialLikesCount={post.likes_count ?? post.reaction_count ?? 0}
            commentsCount={commentCount}
            sharesCount={post.shares_count || 0}
            onAuthRequired={openLogin}
          />
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowComments((v) => !v)}
              data-testid={`eddy-post-comments-toggle-${post.id}`}
              className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/50 transition-colors hover:bg-white/5 hover:text-white/80"
            >
              <MessageCircle className="h-3.5 w-3.5" />
              {showComments ? t.posts_comments_hide : t.posts_comments_show}
            </button>
            {/* SharePost reutilizado del feed principal — mismo poster canvas,
                mismos intents X/FB/WA/TG, mismos OG tags vía /api/og/post/{id}.
                El backend ahora detecta channel='eddy' y hace canonical_spa_url
                apuntar a /rebel-by-eddy en vez de /shadow/post/{id}. */}
            <div data-testid={`eddy-post-share-${post.id}`}>
              <SharePost analysis={post} />
            </div>
          </div>
        </div>

        {showComments && (
          <EddyComments
            postId={post.id}
            user={user}
            t={t}
            onLogin={openLogin}
            onCountChange={setCommentCount}
          />
        )}
      </div>
    </li>
  );
};

// ══════════════════════════════════════════════════════════════════════
// COMMENTS — reutiliza los mismos endpoints /shadow-posts/{id}/comments
// ══════════════════════════════════════════════════════════════════════
const EddyComments = ({ postId, user, t, onLogin, onCountChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/shadow-posts/${postId}/comments`);
      setItems(data.comments || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { onLogin?.(); return; }
    const clean = text.trim();
    if (!clean) return;
    setPosting(true);
    setErr("");
    try {
      const { data } = await axios.post(
        `${API}/shadow-posts/${postId}/comments`,
        { text: clean },
        { withCredentials: true }
      );
      if (data?.comment) {
        setItems((prev) => [...prev, data.comment]);
        setText("");
        onCountChange?.((c) => c + 1);
      }
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Error al comentar");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div
      data-testid={`eddy-post-comments-${postId}`}
      className="mt-3 rounded-xl border border-white/10 bg-black/40 p-3"
    >
      <ul className="mb-3 max-h-64 space-y-2 overflow-y-auto">
        {loading ? (
          <li className="text-center text-[11px] text-white/40">…</li>
        ) : items.length === 0 ? (
          <li className="text-center text-[11px] text-white/40">{t.comments_first}</li>
        ) : (
          items.map((c) => (
            <li
              key={c.id}
              data-testid={`eddy-comment-${c.id}`}
              className="rounded-md border border-white/5 bg-white/[0.02] px-2.5 py-1.5"
            >
              <div className="flex items-baseline gap-2">
                <span className="text-[11px] font-bold text-white/85">
                  {c.user_name || "anon"}
                </span>
                {c.user_username && (
                  <span className="font-mono text-[10px] text-white/35">@{c.user_username}</span>
                )}
              </div>
              <p className="mt-0.5 whitespace-pre-wrap text-[12px] text-white/70">{c.text}</p>
            </li>
          ))
        )}
      </ul>
      <form onSubmit={submit} className="flex items-center gap-2">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={user ? t.comments_input_authed_ph : t.comments_input_guest_ph}
          maxLength={280}
          disabled={!user || posting}
          data-testid={`eddy-comment-input-${postId}`}
          className="flex-1 rounded-md border border-white/10 bg-black/40 px-2.5 py-1.5 text-xs text-white outline-none focus:border-amber-400/50 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={posting || !text.trim()}
          data-testid={`eddy-comment-submit-${postId}`}
          className="inline-flex items-center gap-1 rounded-md border border-amber-400/45 bg-amber-500/[0.14] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-200 transition hover:bg-amber-500/[0.22] disabled:opacity-40"
        >
          {posting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          {t.comments_send_btn}
        </button>
      </form>
      {err && <p className="mt-1 text-[11px] text-red-400">{err}</p>}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// COCINA EN VIVO
// ══════════════════════════════════════════════════════════════════════
const CocinaEnVivoSection = ({ profile, isEddyAdmin, t, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");
  const [titleDraft, setTitleDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const youtubeId = profile?.youtube_id;
  const youtubeTitle = profile?.youtube_title || "";

  const openEdit = () => {
    setUrlDraft(youtubeId ? `https://youtu.be/${youtubeId}` : "");
    setTitleDraft(youtubeTitle);
    setErr("");
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const { data } = await axios.put(
        `${API}/eddy/profile`,
        { youtube_url: urlDraft, youtube_title: titleDraft },
        { withCredentials: true }
      );
      onUpdate(data);
      setEditing(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "URL inválida");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/eddy/profile`,
        { youtube_url: "", youtube_title: "" },
        { withCredentials: true }
      );
      onUpdate(data);
      setEditing(false);
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="eddy-cocina-section" className="scroll-mt-24">
      <div className="flex items-center justify-between gap-3">
        <SectionTitle title={t.cocina_section_title} hint={t.cocina_section_hint} />
        {isEddyAdmin && !editing && (
          <button
            type="button"
            onClick={openEdit}
            data-testid="eddy-cocina-edit-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-white/12 bg-white/[0.04] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/60 transition hover:border-amber-400/40 hover:text-amber-200"
          >
            <Pencil className="h-3 w-3" /> {t.cocina_edit_btn}
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-4 space-y-3 rounded-2xl border border-amber-400/25 bg-amber-500/[0.03] p-5">
          <label className="block">
            <span className="font-mono text-[10px] font-black uppercase tracking-[0.22em] text-amber-400">
              {t.cocina_url_label}
            </span>
            <input
              type="text"
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              placeholder={t.cocina_url_ph}
              data-testid="eddy-cocina-url-input"
              className="mt-1 w-full rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
            />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] font-black uppercase tracking-[0.22em] text-amber-400">
              {t.cocina_title_label}
            </span>
            <input
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              placeholder={t.cocina_title_ph}
              maxLength={200}
              data-testid="eddy-cocina-title-input"
              className="mt-1 w-full rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
            />
          </label>
          {err && <p className="text-xs text-red-400" data-testid="eddy-cocina-error">{err}</p>}
          <div className="flex flex-wrap justify-end gap-2">
            {youtubeId && (
              <button
                type="button"
                onClick={clear}
                disabled={saving}
                data-testid="eddy-cocina-clear-btn"
                className="inline-flex items-center gap-1.5 rounded-md border border-red-400/25 bg-red-500/[0.06] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-red-300 transition hover:bg-red-500/[0.14]"
              >
                <Trash2 className="h-3 w-3" /> {t.cocina_clear_btn}
              </button>
            )}
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              data-testid="eddy-cocina-cancel-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-white/12 bg-white/[0.04] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white/60 transition hover:text-white"
            >
              <X className="h-3 w-3" /> {t.cocina_cancel_btn}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="eddy-cocina-save-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/50 bg-amber-500/[0.14] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-200 transition hover:bg-amber-500/[0.22] disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              {t.cocina_save_btn}
            </button>
          </div>
        </div>
      ) : youtubeId ? (
        <div className="mt-4" data-testid="eddy-cocina-embed">
          {youtubeTitle && (
            <p className="mb-2 text-sm font-semibold text-white/85" data-testid="eddy-cocina-title">
              {youtubeTitle}
            </p>
          )}
          <div className="aspect-video w-full overflow-hidden rounded-2xl border border-white/10 bg-black">
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}`}
              title={youtubeTitle || t.cocina_iframe_fallback_title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="h-full w-full"
              data-testid="eddy-cocina-iframe"
            />
          </div>
        </div>
      ) : (
        <div
          data-testid="eddy-cocina-empty"
          className="mt-4 flex flex-col items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-14 text-center"
        >
          <Play className="h-8 w-8 text-white/25" />
          <p className="text-sm text-white/55">
            {t.cocina_empty}
          </p>
        </div>
      )}
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════════
// EPB GLOBAL F&B — consulting block with services + contact form
// ══════════════════════════════════════════════════════════════════════
const EpbGlobalSection = ({ profile, t, lang }) => {
  // Locale-aware resolution: prefer profile.<field>_i18n[lang], else legacy
  // monolingual field, else i18n canonical default.
  const intro = resolveI18n(profile, "epb_intro_i18n", "epb_intro", lang, "") || t.epb_intro_default;
  const rawServices = resolveI18n(profile, "epb_services_i18n", "epb_services", lang, []);
  const services = (Array.isArray(rawServices) && rawServices.length > 0)
    ? rawServices
    : (t.epb_services_default || []);

  return (
    <section data-testid="eddy-epb-section" className="scroll-mt-24">
      <SectionTitle title={t.epb_section_title} hint={t.epb_section_hint} />
      <div className="mt-6 space-y-6 rounded-3xl border border-white/8 bg-white/[0.02] p-6 md:p-8">
        {profile?.epb_logo_url && (
          <div
            className="flex justify-center"
            data-testid="eddy-epb-logo-wrapper"
          >
            <div className="w-full max-w-[240px] overflow-hidden rounded-2xl border border-amber-400/20 bg-black/30 p-4 shadow-[0_4px_24px_rgba(251,191,36,0.08)] sm:max-w-[280px]">
              <img
                src={profile.epb_logo_url}
                alt="EPB Global F&B — Food & Beverage Consultancy"
                loading="lazy"
                className="h-auto w-full object-contain"
                data-testid="eddy-epb-logo"
              />
            </div>
          </div>
        )}
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-white/75" data-testid="eddy-epb-intro">
          {intro}
        </p>

        <div>
          <h3 className="font-mono text-[11px] font-black uppercase tracking-[0.22em] text-amber-400">
            {t.epb_services_heading}
          </h3>
          <ol className="mt-3 space-y-3" data-testid="eddy-epb-services">
            {services.map((s, i) => (
              <EpbServiceAccordionItem key={i} index={i} service={s} t={t} />
            ))}
          </ol>
        </div>

        <EpbContactForm t={t} />
      </div>
    </section>
  );
};

// Accordion item: click on the row toggles a smooth reveal of `details`.
// Only one item can be expanded at a time (managed via URL-safe local
// state per section). Short `description` stays visible always; `details`
// slides in below with a rotating chevron indicator.
const EpbServiceAccordionItem = ({ index, service, t }) => {
  const [open, setOpen] = useState(false);
  const hasDetails = typeof service?.details === "string" && service.details.trim().length > 0;
  const label = String(index + 1).padStart(2, "0");
  const toggle = () => hasDetails && setOpen((v) => !v);
  return (
    <li
      data-testid={`eddy-epb-service-${index}`}
      className={`overflow-hidden rounded-xl border transition-colors ${
        open
          ? "border-amber-400/30 bg-amber-500/[0.04]"
          : "border-white/8 bg-black/25 hover:border-white/15"
      }`}
    >
      <button
        type="button"
        onClick={toggle}
        disabled={!hasDetails}
        data-testid={`eddy-epb-service-${index}-toggle`}
        aria-expanded={open}
        aria-controls={`eddy-epb-service-${index}-panel`}
        className={`flex w-full items-start gap-4 p-4 text-left ${
          hasDetails ? "cursor-pointer" : "cursor-default"
        }`}
      >
        <span className="shrink-0 font-mono text-lg font-black text-amber-400/80">
          {label}
        </span>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-bold text-white">{service.title}</h4>
          {service.description && (
            <p className="mt-1 text-[13px] leading-relaxed text-white/60">
              {service.description}
            </p>
          )}
          {hasDetails && (
            <span className="mt-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-400/80">
              {open ? t.epb_service_collapse : t.epb_service_expand}
            </span>
          )}
        </div>
        {hasDetails && (
          <ChevronDown
            className={`mt-1 h-4 w-4 shrink-0 text-amber-400/80 transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
          />
        )}
      </button>
      {hasDetails && (
        <div
          id={`eddy-epb-service-${index}-panel`}
          data-testid={`eddy-epb-service-${index}-details`}
          className={`grid transition-[grid-template-rows] duration-300 ease-out ${
            open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          }`}
        >
          <div className="overflow-hidden">
            <div className="border-t border-amber-400/15 px-4 py-4 pl-16">
              <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-white/75">
                {service.details}
              </p>
            </div>
          </div>
        </div>
      )}
    </li>
  );
};

const EpbContactForm = ({ t }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!name.trim() || !email.trim() || !message.trim() || message.trim().length < 10) {
      setErr(t.epb_contact_err_required);
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setErr(t.epb_contact_err_email);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/eddy/consulting/lead`, {
        name: name.trim(),
        email: email.trim(),
        company: company.trim(),
        message: message.trim(),
      });
      setOk(true);
      setName(""); setEmail(""); setCompany(""); setMessage("");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || t.epb_contact_err_default);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="eddy-epb-contact-form">
      <h3 className="font-mono text-[11px] font-black uppercase tracking-[0.22em] text-amber-400">
        {t.epb_contact_heading}
      </h3>
      <p className="mt-1 text-xs text-white/50">{t.epb_contact_intro}</p>
      {ok ? (
        <div
          data-testid="eddy-epb-contact-success"
          className="mt-4 rounded-xl border border-emerald-400/30 bg-emerald-500/[0.06] p-4 text-sm text-emerald-200"
        >
          {t.epb_contact_success}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder={t.epb_contact_name_ph} maxLength={120}
              data-testid="eddy-epb-name-input"
              className="rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
            />
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder={t.epb_contact_email_ph} maxLength={200}
              data-testid="eddy-epb-email-input"
              className="rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
            />
          </div>
          <input
            type="text" value={company} onChange={(e) => setCompany(e.target.value)}
            placeholder={t.epb_contact_company_ph} maxLength={200}
            data-testid="eddy-epb-company-input"
            className="w-full rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
          />
          <textarea
            value={message} onChange={(e) => setMessage(e.target.value)}
            rows={5} maxLength={3000} placeholder={t.epb_contact_message_ph}
            data-testid="eddy-epb-message-input"
            className="w-full resize-y rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
          />
          {err && <p className="text-xs text-red-400" data-testid="eddy-epb-error">{err}</p>}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              data-testid="eddy-epb-submit"
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/50 bg-amber-500/[0.14] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.22em] text-amber-200 transition hover:bg-amber-500/[0.22] disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              {submitting ? t.epb_contact_sending : t.epb_contact_submit}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Shared UI
// ══════════════════════════════════════════════════════════════════════
const SectionTitle = ({ title, hint }) => (
  <div>
    <h2 className="font-mono text-base font-black uppercase tracking-[0.22em] text-white md:text-lg">
      {title}
    </h2>
    {hint && (
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.24em] text-amber-400/70">
        {hint}
      </p>
    )}
  </div>
);

export default RebelByEddyPage;
